/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Settings Profile component to change Password
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.3.0
 */

import { useCallback } from 'react';

import { LinkBlock } from '../../../../../components/common';
import { IPageSettingsTranslation } from '../../../../../modules/internationalization/types/pages/pageSettings';
import { IModalOpenPayload } from '../../../../../modules/modal/types/modalActionTypes';
import { IModalRenderProps } from '../../../../../modules/modal/types/modalTypes';
import { PageSettingsHeaderStyled } from '../../styled/PageSettingsStyled';
import { ESettingsProfileComponents } from '../settingsProfileConstants';
import { ISettingsProfileMetaPassword, ISettingsProfileResetParamsActionPayload } from '../settingsProfileTypes';
import { SettingsProfileLabelStyled, SettingsProfilePasswordContainerStyled } from '../styled/SettingsProfileStyled';
import SettingsProfilePasswordModal from './SettingsProfilePasswordModal';


export interface ISettingsProfilePasswordProps {
    externalUrl?: string;
    meta: ISettingsProfileMetaPassword;
    openModal: (payload: IModalOpenPayload) => void;
    resetParams: (payload: ISettingsProfileResetParamsActionPayload) => void;
    settingsTranslation: IPageSettingsTranslation;
}


export const SettingsProfilePassword = (props: ISettingsProfilePasswordProps) => {

    const {
        resetParams,
        openModal,
        externalUrl,
    } = props;

    const { password: translationPassword } = props.settingsTranslation.profile;

    const resetPasswordParams = useCallback(() => {
        resetParams({ params: [ESettingsProfileComponents.PASSWORD] });
    }, [resetParams]);

    const handleLinkClick = useCallback(() => {
        if (externalUrl) {
            window.open(externalUrl, '_blank');
            return;
        }
        openModal({
            modalParams: {
                isCustomCrossButton: true,
            },
            render: (props: IModalRenderProps) => <SettingsProfilePasswordModal
                onClose={() => {
                    props.onCloseModal();
                    resetPasswordParams();
                }}
            />,
        });
    }, [resetPasswordParams, externalUrl, openModal]);

    return (
        <SettingsProfilePasswordContainerStyled>
            <PageSettingsHeaderStyled>
                {translationPassword.header}
            </PageSettingsHeaderStyled>
            <SettingsProfileLabelStyled>
                {translationPassword.subheader}
            </SettingsProfileLabelStyled>
            <LinkBlock dataName="changePasswordLink" onClick={handleLinkClick}>
                {translationPassword.link}
            </LinkBlock>
        </SettingsProfilePasswordContainerStyled>
    );
};
